// Shared date-range presets used across reports + warehouse dashboards.
// Strings are local-date YYYY-MM-DD (no timezone shift) — matches the API
// contract for date_from / date_to query parameters.

export type DatePreset =
    | 'today'
    | 'yesterday'
    | '7d'
    | '30d'
    | 'this_week'
    | 'month'
    | 'last_month'
    | 'quarter'
    | 'year'

// Period preset cho endpoint thống kê nhập/xuất (khớp BE timeutil.ResolvePeriod).
export type StatsPeriodPreset =
    | 'today'
    | 'yesterday'
    | 'last_n_days'
    | 'this_week'
    | 'this_month'
    | 'custom'

export interface DateRange {
    from: string
    to: string
}

// Format Date → 'YYYY-MM-DD' using LOCAL components, NOT toISOString().
// toISOString() converts to UTC first; in TZ with positive offset (e.g. ICT
// +07:00) local midnight becomes the previous day in UTC, so the legacy
// implementation silently rolled every preset back one day.
export function toLocalYMD(d: Date): string {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

// vnCivilDate trả về NGÀY LỊCH Việt Nam của một thời điểm, đóng gói trong một
// Date có phần giờ địa phương bằng nửa đêm — để mọi phép cộng trừ ngày sẵn có
// (setDate, getDay, getMonth) chạy trên đúng ngày VN.
//
// Vì sao không dùng thẳng getFullYear/getMonth/getDate của Date: chúng đọc theo
// đồng hồ MÁY NGƯỜI DÙNG. Máy ở Việt Nam thì trùng, nhưng một máy đặt sai múi
// giờ, một laptop mang đi công tác, hay runner CI chạy UTC đều làm bộ lọc
// "Hôm nay" nhảy sang ngày khác. Ngày lịch của doanh nghiệp luôn là giờ VN.
export function vnCivilDate(instant: Date = new Date()): Date {
    // en-CA cho ra sẵn dạng YYYY-MM-DD, khỏi phải ghép tay.
    const [y, m, d] = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Ho_Chi_Minh',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    })
        .format(instant)
        .split('-')
        .map(Number)
    return new Date(y, m - 1, d)
}

/**
 * Hôm nay dạng 'YYYY-MM-DD' theo lịch Việt Nam.
 *
 * Dùng hàm này cho mọi giá trị mặc định "hôm nay" (ô ngày trên form, mốc lọc
 * báo cáo, tên file xuất). KHÔNG dùng `new Date().toISOString().slice(0, 10)`:
 * chuỗi đó là ngày theo UTC, nên từ 00:00 đến 06:59 giờ VN (UTC+7) nó trả về
 * NGÀY HÔM QUA. Cũng không đọc trực tiếp giờ máy: xem vnCivilDate.
 */
export function todayYMD(): string {
    return toLocalYMD(vnCivilDate())
}

/** Giờ:phút hiện tại theo giờ Việt Nam, dạng 'HH:mm' — mặc định cho ô giờ
 *  trên form nhập khoản chi mới. */
export function nowHHmmVN(): string {
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Ho_Chi_Minh',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date())
}

/** Giờ:phút theo giờ Việt Nam của một mốc ISO — dùng để prefill ô giờ khi
 *  sửa một khoản chi đã có `spent_at`. Input rỗng/không hợp lệ → ''. */
export function hhmmFromInstantVN(iso: string | undefined): string {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Ho_Chi_Minh',
        hour: '2-digit',
        minute: '2-digit',
    }).format(d)
}

/** Ghép ngày ('YYYY-MM-DD') + giờ ('HH:mm', cả hai đọc theo lịch/giờ Việt
 *  Nam) thành một mốc thời gian tuyệt đối (ISO 8601 có offset +07:00) để
 *  gửi lên server là `spent_at`. `time` rỗng/undefined → không biết giờ
 *  thật, trả về `undefined` (không tự vẽ "00:00"). */
export function toVNInstant(
    date: string,
    time: string | undefined,
): string | undefined {
    if (!date || !time) return undefined
    return `${date}T${time}:00+07:00`
}

export function getDefaultDateRange(): DateRange {
    const now = vnCivilDate()
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    return {
        from: toLocalYMD(firstDay),
        to: toLocalYMD(now),
    }
}

// lastNDaysRange trả [today-(N-1), today], clamp N vào 1..365.
export function lastNDaysRange(n: number): DateRange {
    const today = vnCivilDate()
    const days = Math.min(365, Math.max(1, Math.floor(n) || 7))
    const d = new Date(today)
    d.setDate(d.getDate() - (days - 1))
    return { from: toLocalYMD(d), to: toLocalYMD(today) }
}

// thisWeekRange trả [thứ Hai tuần này, today] (Monday start).
export function thisWeekRange(): DateRange {
    const today = vnCivilDate()
    // getDay(): 0=CN..6=T7. Monday-start offset.
    const offset = (today.getDay() + 6) % 7
    const monday = new Date(today)
    monday.setDate(monday.getDate() - offset)
    return { from: toLocalYMD(monday), to: toLocalYMD(today) }
}

export function getPresetRange(preset: DatePreset | string): DateRange {
    const today = vnCivilDate()
    const now = today
    switch (preset) {
        case 'today':
            return { from: toLocalYMD(today), to: toLocalYMD(today) }
        case 'yesterday': {
            const d = new Date(today)
            d.setDate(d.getDate() - 1)
            return { from: toLocalYMD(d), to: toLocalYMD(d) }
        }
        case 'this_week':
            return thisWeekRange()
        case '7d': {
            const d = new Date(today)
            d.setDate(d.getDate() - 6)
            return { from: toLocalYMD(d), to: toLocalYMD(today) }
        }
        case '30d': {
            const d = new Date(today)
            d.setDate(d.getDate() - 29)
            return { from: toLocalYMD(d), to: toLocalYMD(today) }
        }
        case 'month':
            return {
                from: toLocalYMD(
                    new Date(now.getFullYear(), now.getMonth(), 1),
                ),
                to: toLocalYMD(today),
            }
        case 'last_month': {
            // Ngày 0 của tháng này = ngày cuối tháng trước (tự xử lý tháng 1).
            const lastDay = new Date(now.getFullYear(), now.getMonth(), 0)
            return {
                from: toLocalYMD(
                    new Date(lastDay.getFullYear(), lastDay.getMonth(), 1),
                ),
                to: toLocalYMD(lastDay),
            }
        }
        case 'quarter': {
            const qMonth = Math.floor(now.getMonth() / 3) * 3
            return {
                from: toLocalYMD(new Date(now.getFullYear(), qMonth, 1)),
                to: toLocalYMD(today),
            }
        }
        case 'year':
            return {
                from: toLocalYMD(new Date(now.getFullYear(), 0, 1)),
                to: toLocalYMD(today),
            }
        default:
            return getDefaultDateRange()
    }
}
